import { isString, object, toSeconds } from '@art-suite/art-core-ts';
import { useMemo } from 'react';

import {
  activeScanLifecycleStatusOptions,
  ApiScan,
  ApiUpdateScan,
  getActiveScansQueryKey,
  getScanQueryKey,
  getScansByPropertyProjectIdQueryKey,
  getScansForUserQueryKey,
  getScansQueryPrefix,
  KaiProApiClient,
} from '@kaiizen/kai-pro-api';
import { QueryCache, useCachedQuery, UseCachedQueryResult, useQueryCache, useQueryCacheMutation } from '@kaiizen/react-persistent-cache';

import { useUpdateMutatorsWithQueryCache } from '../reactQueryLib';
import { useKaiProApiClient } from '../useKaiProApiClient';
import { ScanOfflineDataMinTtlOnAccessMs } from './ScanStateLib';
import { useScanDataDeltaFetch } from './useScanData';

export type ScanMutators = {
  vivifyChangeRequest: (propertyProjectId: string) => Promise<ApiScan>;
  vivifyProgressWalk: (propertyProjectId: string) => Promise<ApiScan>;
  update: (id: string, updates: ApiUpdateScan) => Promise<ApiScan>;
  cancelChangeOrder: (scanId: string) => Promise<ApiScan>;
};

export const useScanMutators = (
  kaiProApiClient: KaiProApiClient,
) => {
  const queryCache = useQueryCache<ApiScan>();
  const fetchLatestScanDataDelta = useScanDataDeltaFetch();
  const basicScanMutators = useUpdateMutatorsWithQueryCache({
    apiUpdate: kaiProApiClient.updateScan.bind(kaiProApiClient),
    pluralKeyPrefix: getScansQueryPrefix(),
    getSingleKey: getScanQueryKey,
  });

  const cancelChangeOrder = useQueryCacheMutation(
    async (scanId: string): Promise<ApiScan> => kaiProApiClient.cancelChangeOrder(scanId),
    {
      onSuccess: async (scan: ApiScan) => {
        await queryCache.cache.set(getScanQueryKey(scan.id), scan);
        await queryCache.refetchAll({ prefix: getScansQueryPrefix() });
        // Refetch scan data to get the reverted changes
        await fetchLatestScanDataDelta(scan.id);
      },
    },
  ).mutate;

  return useMemo(() => {
    const getLatestScopeScan = async (propertyProjectId: string) => {
      const allProjectScans = await kaiProApiClient.getScansByPropertyProjectId(propertyProjectId);
      let latestScan = allProjectScans[0];
      for (const scan of allProjectScans) {
        if (toSeconds(scan.updatedAt) > toSeconds(latestScan.updatedAt)) {
          latestScan = scan;
        }
      }
      return latestScan;
    };
    return {
      ...basicScanMutators,
      cancelChangeOrder,
      vivifyChangeRequest: async (propertyProjectId: string) => {
        const latestScan = await getLatestScopeScan(propertyProjectId);
        if (!latestScan) throw new Error('No latest scan found');
        return latestScan;
      },
      vivifyProgressWalk: async (propertyProjectId: string) => {
        const latestScan = await getLatestScopeScan(propertyProjectId);
        if (!latestScan) throw new Error('No latest scan found');
        return latestScan;
      },
    };
  }, [basicScanMutators, kaiProApiClient, queryCache, fetchLatestScanDataDelta, cancelChangeOrder]);
};

const fetchAndPopulateSingletonQueries = async (scansPromise: Promise<ApiScan[]>, queryCache: QueryCache<ApiScan>): Promise<ApiScan[]> => {
  const scans = await scansPromise;
  for (const scan of scans) {
    queryCache.cache.set(getScanQueryKey(scan.id), scan);
  }
  return scans;
};

const useScansBase = (queryKey: string, queryFn: () => Promise<any[]>, enabled: boolean, options?: { prefetchOnly?: boolean }) => {
  const kaiProApiClient = useKaiProApiClient();
  const query = useCachedQuery<ApiScan[]>(
    queryKey,
    async (key, queryCache) => fetchAndPopulateSingletonQueries(queryFn(), queryCache as unknown as QueryCache<ApiScan>),
    { enabled, prefetchOnly: options?.prefetchOnly ?? false, minTtlOnAccessMs: ScanOfflineDataMinTtlOnAccessMs },
  );

  return {
    ...query,
    scans: query.data,
    scansById: query.data ? object(query.data, { withKey: ({ id }) => id }) : undefined,
    scanMutators: useScanMutators(kaiProApiClient),
  };
};

export const useActiveScans = (options?: { archived?: boolean }) => {
  const kaiProApiClient = useKaiProApiClient();
  return useScansBase(
    getActiveScansQueryKey(options),
    () => kaiProApiClient.getScans({ scanLifecycleStatuses: activeScanLifecycleStatusOptions, archived: options?.archived }),
    !!kaiProApiClient.token,
  );
};

export const useScansByPropertyProjectId = (
  propertyProjectId: string | undefined,
  options?: { archived?: boolean; includeAudienceDocs?: boolean; prefetchOnly?: boolean },
) => {
  const kaiProApiClient = useKaiProApiClient();
  return useScansBase(
    getScansByPropertyProjectIdQueryKey(propertyProjectId, options),
    () => kaiProApiClient.getScansByPropertyProjectId(propertyProjectId!, { archived: options?.archived, includeAudienceDocs: options?.includeAudienceDocs }),
    !!kaiProApiClient.token && !!propertyProjectId,
    options,
  );
};

export const useScansForUser = (userId: string | undefined) => {
  const kaiProApiClient = useKaiProApiClient();
  return useScansBase(
    getScansForUserQueryKey(userId),
    () => kaiProApiClient.getScansForUser(userId!),
    !!kaiProApiClient.token && !!userId,
  );
};

/** Cached query is always ApiScan; only `scan` (mirror of `data`) is narrowed per overload. */
type UseScanResult<TScan> = UseCachedQueryResult<ApiScan> & {
  scan: TScan;
  scanMutators: ReturnType<typeof useScanMutators>;
};

/**
 * Hook that returns the scan data for a given scan id.
 * Note: this ONLY returns the Scan record - not ScanRoot (scan data) - use useScanRoot (read-only) or useScanContext (with mutators)
 * @param scanIdOrScan - The id of the scan to get the data for, or the scan object itself if already fetched. Providing the scan itself guarantees that useScan always returns a valid scan. It will also warm the cache if it isn't already in the cache. the main reason for useScan when you already have the scan is to subscribe to future changes.
 */
export function useScan(scanIdOrScan: ApiScan): UseScanResult<ApiScan>;
export function useScan(scanIdOrScan: undefined): UseScanResult<undefined>;
export function useScan(scanIdOrScan: string | undefined): UseScanResult<ApiScan | undefined>;
export function useScan(scanIdOrScan: ApiScan | undefined): UseScanResult<ApiScan | undefined>;
export function useScan(scanIdOrScan: ApiScan | string): UseScanResult<ApiScan | undefined>;
export function useScan(scanIdOrScan: ApiScan | string | undefined): UseScanResult<ApiScan | undefined>;
export function useScan(scanIdOrScan: string | undefined | ApiScan): UseScanResult<ApiScan | undefined> {
  const scanId = isString(scanIdOrScan) ? scanIdOrScan : scanIdOrScan?.id;
  const preloadData = (scanIdOrScan && scanIdOrScan !== scanId) ? scanIdOrScan as ApiScan : undefined;
  const kaiProApiClient = useKaiProApiClient();
  const scanQueryKey = getScanQueryKey(scanId);
  const scanQuery = useCachedQuery<ApiScan>(
    scanQueryKey,
    () => kaiProApiClient.getScan(scanId!),
    {
      enabled: !!kaiProApiClient.token && !!scanId,
      preloadData,
      minTtlOnAccessMs: ScanOfflineDataMinTtlOnAccessMs,
    },
  );
  return {
    ...scanQuery,
    scan: scanQuery.data,
    scanMutators: useScanMutators(kaiProApiClient),
  };
}
